import * as React from "react";
import {
  DatagridBody,
  DatagridConfigurable,
  Empty,
  FunctionField,
  NumberField,
  Pagination,
  RecordContextProvider,
  TextField,
  useListContext,
  useDataProvider,
  useRecordContext,
  useTranslate,
} from "react-admin";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import incomeStatementFilterList from "../../components/incomeStatements/filter/incomeStatementFilterList";
import getFirstAndLastDateOfCurrentMonth from "../../utils/firstLastDate";
import IncomeStatementAsideList from "../../components/incomeStatements/aside/IncomeStatementAsideList";
import DownloadAction from "../../components/tableListActions/DownloadActionNew";
import ManualReportList from "../../components/reports/ManualReportList";
import useCompanyScopedListFilters from "../../hooks/useCompanyScopedListFilters";
import {
  capitalizeAllWordsUppercaseLast,
  toCurrencyAccounting,
} from "../../utils/formatter";

const [firstDate, lastDate] = getFirstAndLastDateOfCurrentMonth();

const normalizeIncomeStatementGroupKey = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const MyDatagridRow = ({
  record,
  id,
  onToggleItem,
  children,
  hasBulkActions,
  rowClick,
  selected,
  selectable,
  ...props
}) => {
  return id ? (
    <RecordContextProvider value={record}>
      <TableRow {...props}>
        {/* data columns based on children */}
        {React.Children.map(children, (child) => {
          return React.isValidElement(child) && child.props.source ? (
            <TableCell
              key={`${id}-${child.props.source}`}
              align={child.type.textAlign || child.props.textAlign}
            >
              {child}
            </TableCell>
          ) : null;
        })}
      </TableRow>
    </RecordContextProvider>
  ) : null;
};

const MyDatagridBody = (props) => (
  <DatagridBody {...props} row={<MyDatagridRow />} />
);
const MyDatagridConfigurable = (props) => (
  <DatagridConfigurable {...props} hover body={<MyDatagridBody />} />
);

export default function IncomeStatementList() {
  const { permissions, filterDefaultValues } = useCompanyScopedListFilters({
    view_mode: "grouped",
    start_date: firstDate,
    end_date: lastDate,
  });

  React.useEffect(() => {
    // Add class to #root when this component mounts
    const raLayoutContentWithSidebarElement = document.querySelector(
      "main.RaLayout-contentWithSidebar"
    );
    if (raLayoutContentWithSidebarElement) {
      raLayoutContentWithSidebarElement.classList.add(
        "only-for-income-statement"
      );
    }

    // Remove the class when the component unmounts
    return () => {
      if (raLayoutContentWithSidebarElement) {
        raLayoutContentWithSidebarElement.classList.remove(
          "only-for-income-statement"
        );
      }
    };
  }, []);

  const list = React.useMemo(
    () => (
      <ManualReportList
        perPage={200}
        pagination={<Pagination rowsPerPageOptions={[200]} />}
        aside={
          null
          // <IncomeStatementAsideList
          //   defaultFilter={{
          //     start_date: firstDate,
          //     end_date: lastDate,
          //   }}
          // />
        }
        title="mine.label.income_statement"
        empty={false}
        actions={<DownloadAction />}
        filters={incomeStatementFilterList(permissions)}
        filterDefaultValues={filterDefaultValues}
        requiredFilters={["view_mode", "start_date", "end_date"]}
        resetClearsReport
        sort={{ field: "chart_of_accounts.code", order: "ASC" }}
        sx={{ "& .RaList-main": { width: "100%" } }}
        // queryOptions={{ meta: { sort: ["transactions.code ASC"] } }} // overide default sort
      >
        <CoaCategories />
      </ManualReportList>
    ),
    [permissions, filterDefaultValues]
  );

  return list;
}

const CoaCategories = () => {
  const { data, filterValues, setFilters } = useListContext();
  const dataProvider = useDataProvider();
  const t = useTranslate();
  const [groupsByNormalizedName, setGroupsByNormalizedName] = React.useState(
    {}
  );
  const [groupsById, setGroupsById] = React.useState({});

  React.useEffect(() => {
    let active = true;

    dataProvider
      .getList("income-statements/category-groups", {
        pagination: { page: 1, perPage: 200 },
        sort: {
          field: "income_statement_category_groups.display_order",
          order: "ASC",
        },
        filter: {},
      })
      .then(({ data: groups }) => {
        if (!active) {
          return;
        }

        const nextGroupsByNormalizedName = {};
        const nextGroupsById = {};
        for (const group of groups) {
          const normalizedKey = normalizeIncomeStatementGroupKey(group.name);
          if (!normalizedKey) {
            continue;
          }
          nextGroupsByNormalizedName[normalizedKey] = group;
          nextGroupsById[String(group.id)] = group;
        }
        setGroupsByNormalizedName(nextGroupsByNormalizedName);
        setGroupsById(nextGroupsById);
      })
      .catch(() => {
        if (!active) {
          return;
        }
        setGroupsByNormalizedName({});
        setGroupsById({});
      });

    return () => {
      active = false;
    };
  }, [dataProvider]);

  const isGroupedMode =
    String(filterValues?.view_mode || "grouped").toLowerCase() === "grouped";
  const selectedGroupId = Array.isArray(filterValues?.group_ids)
    ? filterValues.group_ids[0]
    : filterValues?.group_ids;
  const selectedGroup = selectedGroupId
    ? groupsById[String(selectedGroupId)]
    : undefined;

  const showGroupedSummary = React.useCallback(() => {
    const nextFilters = {
      ...filterValues,
      view_mode: "grouped",
    };
    delete nextFilters.group_ids;
    delete nextFilters.category_ids;
    setFilters(nextFilters, undefined, false);
  }, [filterValues, setFilters]);

  React.useEffect(() => {
    if (
      isGroupedMode &&
      (filterValues?.group_ids?.length || filterValues?.category_ids?.length)
    ) {
      const nextFilters = { ...filterValues };
      delete nextFilters.group_ids;
      delete nextFilters.category_ids;
      setFilters(nextFilters, undefined, false);
    }
  }, [filterValues, isGroupedMode, setFilters]);

  const drillDownToDetailGroup = React.useCallback(
    (headerKey) => {
      if (!isGroupedMode) {
        return;
      }

      const group =
        groupsByNormalizedName[normalizeIncomeStatementGroupKey(headerKey)];
      if (!group) {
        return;
      }

      setFilters(
        {
          ...filterValues,
          view_mode: "detail",
          group_ids: [group.id],
          category_ids: [],
        },
        undefined,
        false
      );
    },
    [filterValues, groupsByNormalizedName, isGroupedMode, setFilters]
  );

  if (!data) {
    return <Empty />;
  }

  return (
    <Box
      sx={{
        overflowX: "auto",
        overflowY: "auto",
        height: "calc(100vh - 160px)",
      }}
    >
      <Box
        sx={{
          alignItems: { xs: "flex-start", sm: "center" },
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
          justifyContent: "space-between",
          px: 2,
          py: 1,
          position: "sticky",
          left: 0,
          zIndex: 12,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {isGroupedMode
            ? "Grouped menampilkan ringkasan per grup produk. Klik nama grup pada header untuk melihat detailnya."
            : selectedGroup
            ? `Detail grup: ${selectedGroup.name}`
            : "Detail seluruh kategori produk."}
        </Typography>
        {!isGroupedMode && (
          <Button
            size="small"
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={showGroupedSummary}
          >
            Kembali ke Ringkasan Grouped
          </Button>
        )}
      </Box>
      <Box
        component="table"
        sx={{
          borderCollapse: "collapse",
          bgcolor: (theme) => theme.palette.background.paper,
          tableLayout: "fixed",
          width: "fit-content",
          "& td, th": {
            p: "10px",
            borderBottom: (theme) => "1px solid " + theme.palette.divider,
          },
          "& th": {
            bgcolor: (theme) => theme.palette.background.paper,
          },
          "& th.sticky-top": {
            position: "sticky",
            top: 0,
          },
          "& th:nth-child(-n+2)": {
            zIndex: 10,
          },
        }}
      >
        <thead>
          <tr>
            <Box
              className="sticky-top"
              component="th"
              sx={{
                width: "150px",
                position: "sticky",
                left: 0,
                textAlign: "left",
                backgroundColor: (theme) => theme.palette.background.paper,
              }}
            >
              <Typography color="primary">{t("mine.label.code")}</Typography>
            </Box>
            <Box
              className="sticky-top"
              component="th"
              sx={{
                width: "390px",
                position: "sticky",
                left: "151px",
                textAlign: "left",
                backgroundColor: (theme) => theme.palette.background.paper,
              }}
            >
              <Typography color="primary">{t("mine.label.name")}</Typography>
            </Box>
            {data[0].transaction_keys.map((key, idx) => {
              const canDrillDown =
                isGroupedMode &&
                Boolean(
                  groupsByNormalizedName[
                    normalizeIncomeStatementGroupKey(key)
                  ]
                );

              return (
                <React.Fragment key={key + idx}>
                  <th
                    className="sticky-top"
                    style={{ width: "150px", textAlign: "right" }}
                  >
                    {canDrillDown ? (
                      <Tooltip title={`Lihat rincian ${capitalizeAllWordsUppercaseLast(key)}`}>
                        <Typography
                          aria-label={`Lihat rincian ${capitalizeAllWordsUppercaseLast(key)}`}
                          color="primary"
                          component="span"
                          onClick={() => drillDownToDetailGroup(key)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              drillDownToDetailGroup(key);
                            }
                          }}
                          role="button"
                          sx={{
                            cursor: "pointer",
                            textDecoration: "underline",
                            "&:focus-visible": {
                              borderRadius: 0.5,
                              outline: "2px solid currentColor",
                              outlineOffset: 2,
                            },
                          }}
                          tabIndex={0}
                        >
                          {capitalizeAllWordsUppercaseLast(key)}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography color="primary">
                        {capitalizeAllWordsUppercaseLast(key)}
                      </Typography>
                    )}
                  </th>
                  <th
                    className="sticky-top"
                    style={{ width: "70px", textAlign: "center" }}
                  >
                    <Typography color="primary">%</Typography>
                  </th>
                </React.Fragment>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {data.map((val, idx) => {
            let pad = { paddingLeft: "10px" };
            switch (val.level) {
              case 2:
                pad.paddingLeft = "20px !important";
                break;
              case 3:
                pad.paddingLeft = "30px !important";
                break;
              case 4:
                pad.paddingLeft = "40px !important";
                break;
              case 5:
                pad.paddingLeft = "50px !important";
                break;
              default:
                pad.paddingLeft = "10px !important";
                break;
            }

            let fontWeight = 400;
            if (val.name === "") {
              fontWeight = 700;
            }
            return (
              <tr key={val.id}>
                <Box
                  component="td"
                  colSpan={val.name ? 1 : 2}
                  sx={{
                    position: "sticky",
                    left: 0,
                    backgroundColor: (theme) => theme.palette.background.paper,
                    ...fontWeight,
                    ...pad,
                  }}
                >
                  <Typography variant="body2" fontWeight={fontWeight}>
                    {val.code}
                  </Typography>
                </Box>
                {val.name && (
                  <Box
                    component="td"
                    sx={{
                      position: "sticky",
                      left: "151px",
                      whiteSpace: "nowrap",
                      backgroundColor: (theme) =>
                        theme.palette.background.paper,
                      ...pad,
                    }}
                  >
                    <Typography variant="body2" fontWeight={fontWeight}>
                      {val.name}
                    </Typography>
                  </Box>
                )}
                {val.transaction_keys
                  ? val.transaction_keys.map((key, idx) => {
                      let amountRecord = {
                        amount: val.transactions[key].amount,
                      };
                      if (
                        typeof amountRecord.amount === "string" &&
                        !isNaN(amountRecord.amount)
                      ) {
                        amountRecord.amount = +amountRecord.amount;
                      }
                      amountRecord.amount = toCurrencyAccounting(
                        amountRecord.amount
                      );
                      return (
                        <React.Fragment key={key + idx}>
                          <td style={{ textAlign: "right" }}>
                            <Typography variant="body2" fontWeight={fontWeight}>
                              {amountRecord.amount}
                            </Typography>
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <Typography variant="body2" fontWeight={fontWeight}>
                              {val.transactions[key].percentage}
                            </Typography>
                          </td>
                        </React.Fragment>
                      );
                    })
                  : null}
              </tr>
            );
          })}
        </tbody>
      </Box>
    </Box>
  );
};
